import { ref, provide, inject, type Ref } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import { usePrevioCore } from '@previo/core'
import { DateTimeRange } from '../model/DateTimeRange'

const DATE_TIME_RANGE_PICKER_KEY = Symbol('DateTimeRangePickerState')

export interface DateTimeRangePickerState {
  range: Ref<DateTimeRange>
  currentView: Ref<Dayjs>
  currentCalendar: () => Dayjs
  secondCalendar: () => Dayjs
  subtractMonth: () => void
  advanceMonth: () => void
  navigateToDate: (date: Dayjs) => void
  currentMonthFormatted: () => string
  nextMonthFormatted: () => string
  setRange: (range: DateTimeRange) => void
  setMin: (date: Dayjs) => void
  setMax: (date: Dayjs) => void
  getMin: () => Dayjs | null
  getMax: () => Dayjs | null
  setMaxRangeDays: (days: number) => void
  getMaxRangeDays: () => number | null
  setDisableClearable: (disable: boolean) => void
  isDisableClearable: () => boolean
  setSelectingFrom: () => void
  setSelectingTo: () => void
  isSelectingFrom: () => boolean
  isSelectingTo: () => boolean
}

const createDateTimeRangePickerState = (): DateTimeRangePickerState => {
  const core = usePrevioCore()

  const range = ref<DateTimeRange>(new DateTimeRange())
  const min = ref<Dayjs | null>(null)
  const max = ref<Dayjs | null>(null)
  const maxRangeDays = ref<number | null>(null)
  const disableClearable = ref<boolean>(false)
  const currentView = ref<Dayjs>(dayjs())
  const selectingFrom = ref<boolean>(false)
  const selectingTo = ref<boolean>(false)

  const currentCalendar = (): Dayjs => {
    return currentView.value
  }

  const secondCalendar = (): Dayjs => {
    return currentView.value.add(1, 'month')
  }

  const subtractMonth = (): void => {
    currentView.value = currentView.value.subtract(1, 'month')
  }

  const advanceMonth = (): void => {
    currentView.value = currentView.value.add(1, 'month')
  }

  const navigateToDate = (date: Dayjs): void => {
    currentView.value = date
  }

  const currentMonthFormatted = (): string => {
    const month = currentView.value.locale(core.useLocale().get()).format('MMMM')
    return month.charAt(0).toUpperCase() + month.slice(1)
  }

  const nextMonthFormatted = (): string => {
    const month = currentView.value.locale(core.useLocale().get()).add(1, 'month').format('MMMM')
    return month.charAt(0).toUpperCase() + month.slice(1)
  }

  const setRange = (newRange: DateTimeRange): void => {
    range.value = newRange

    currentView.value = newRange.from || newRange.to || dayjs()
  }

  const getMin = (): Dayjs | null => {
    return min.value
  }

  const setMin = (date: Dayjs): void => {
    min.value = date
  }

  const getMax = (): Dayjs | null => {
    return max.value
  }

  const setMax = (date: Dayjs): void => {
    max.value = date
  }

  const getMaxRangeDays = (): number | null => {
    return maxRangeDays.value
  }

  const setMaxRangeDays = (days: number): void => {
    maxRangeDays.value = days
  }

  const setDisableClearable = (disable: boolean): void => {
    disableClearable.value = disable
  }

  const isDisableClearable = (): boolean => {
    return disableClearable.value
  }

  const setSelectingFrom = (): void => {
    selectingFrom.value = true
    selectingTo.value = false
  }

  const setSelectingTo = (): void => {
    selectingTo.value = true
    selectingFrom.value = false
  }

  const isSelectingFrom = (): boolean => {
    return selectingFrom.value
  }

  const isSelectingTo = (): boolean => {
    return selectingTo.value
  }

  return {
    range,
    currentView,
    currentCalendar,
    secondCalendar,
    subtractMonth,
    advanceMonth,
    currentMonthFormatted,
    nextMonthFormatted,
    setRange,
    setMin,
    setMax,
    getMin,
    getMax,
    setMaxRangeDays,
    getMaxRangeDays,
    setDisableClearable,
    isDisableClearable,
    setSelectingFrom,
    setSelectingTo,
    isSelectingFrom,
    isSelectingTo,
    navigateToDate,
  }
}

export const provideDateTimeRangePickerState = (): DateTimeRangePickerState => {
  const navigationState = createDateTimeRangePickerState()

  provide(DATE_TIME_RANGE_PICKER_KEY, navigationState)

  return navigationState
}

export const useDateTimeRangePicker = (): DateTimeRangePickerState => {
  const navigationState: DateTimeRangePickerState | undefined = inject(DATE_TIME_RANGE_PICKER_KEY)

  if (!navigationState) {
    throw new Error(
      'useDateTimeRangePicker() must be used within a provideDateTimeRangePickerState() scope',
    )
  }

  return navigationState
}
